import { Tooltip, IconButton } from '@mui/material';

import { Star, StarOutline } from '@mui/icons-material';

import { use, useState } from 'react';

import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';

import { PINNED_POSTS_COLLECTION_QUERY } from '@/app/(main)/g/[machineName]/noticeboard/_components/noticeboard/PinnedPostsCollection';

import { SERVICE_GROUPS_PINNED_POSTS_QUERY } from '@/app/(main)/s/[machineName]/admin/noticeboards/posts/ServiceMultiGroupPinnedPostsAccordion';

import { USERS_GROUPS_POSTS_QUERY } from '@/app/(main)/myHub/home/_components/MultiGroupPostsEntry';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { ServiceAdminMultiGroupContext } from '@/app/(main)/s/[machineName]/admin/contexts/ServiceAdminMultiGroupContext';

import { MultiGroupContext } from '@/app/(main)/myHub/contexts/MultiGroupContext';

import { POST_LIMIT } from '../../PostCollection';

import { PostAddTitleAndPinDialog } from './post-pin-button/PostAddTitleAndPinDialog';

export const PIN_POST_MUTATION = graphql(`
  mutation PinPost($data: PinPostInput) {
    pinPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

export const UNPIN_POST_MUTATION = graphql(`
  mutation UnpinPost($data: UnpinPostInput) {
    unpinPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

const POST_PIN_BUTTON_POST_FRAGMENT = graphql(`
  fragment postPinButtonPostFields on Post {
    ...postFields
    ...postAddTitleAndPinDialogPostFields
  }
`);

const POST_PIN_BUTTON_GROUP_FRAGMENT = graphql(`
  fragment postPinButtonGroupFields on Group {
    ...postAddTitleAndPinDialogGroupFields
    _id
  }
`);

interface Props {
  post: DocumentType<typeof POST_PIN_BUTTON_POST_FRAGMENT>;
  group: DocumentType<typeof POST_PIN_BUTTON_GROUP_FRAGMENT>;
  location?: 'myHub' | 'adminHub' | null;
  serviceMachineName?: string | null;
}

export const PostPinButton = ({
  post,
  group,
  location = null,
  serviceMachineName,
}: Props) => {
  const { _id: postId, title, pinned } = post;
  const { _id: groupId } = group;

  const refetchQueries = [];

  if (location === 'adminHub') {
    const { groupFilterIds } = use(ServiceAdminMultiGroupContext);

    refetchQueries.push({
      query: SERVICE_GROUPS_PINNED_POSTS_QUERY,
      variables: {
        machineName: serviceMachineName || '',
        first: POST_LIMIT,
        after: '',
        groupIds: groupFilterIds,
      },
    });
  } else if (location === 'myHub') {
    const { currentUserId } = use(CurrentUserContext);
    const { groupFilterIds } = use(MultiGroupContext);

    refetchQueries.push({
      query: USERS_GROUPS_POSTS_QUERY,
      variables: {
        userId: currentUserId ?? '',
        first: POST_LIMIT,
        after: '',
        groupIds: groupFilterIds,
      },
    });
  } else {
    refetchQueries.push({
      query: PINNED_POSTS_COLLECTION_QUERY,
      variables: {
        groupId,
      },
    });
  }

  const [pinPost, { loading: pinLoading }] = useMutation(PIN_POST_MUTATION, {
    completionMessage: `"${title}" post pinned to the top of the noticeboard`,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const [unpinPost, { loading: unpinLoading }] = useMutation(
    UNPIN_POST_MUTATION,
    {
      completionMessage: `"${title}" post unpinned from the noticeboard`,
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const [dialogOpen, setDialogOpen] = useState(false);

  const loading = pinLoading || unpinLoading;

  return (
    <div>
      <Tooltip
        title={
          pinLoading
            ? 'Pinning...'
            : unpinLoading
              ? 'Unpinning...'
              : pinned
                ? 'Unpin this post from the top of the noticeboard'
                : 'Pin this post to the top of the noticeboard'
        }
      >
        <span>
          <IconButton
            color="primary"
            size="large"
            disabled={loading}
            onClick={() => {
              if (pinned) {
                unpinPost({
                  variables: {
                    data: {
                      post: postId,
                    },
                  },
                });
              } else if (title) {
                pinPost({
                  variables: {
                    data: {
                      post: postId,
                    },
                  },
                });
              } else {
                setDialogOpen(true);
              }
            }}
          >
            {pinned ? <Star /> : <StarOutline />}
          </IconButton>
        </span>
      </Tooltip>
      <PostAddTitleAndPinDialog
        post={post}
        group={group}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        serviceMachineName={serviceMachineName}
        location={location}
      />
    </div>
  );
};
